// ============================================================================
// PATIENT MANAGER SERVICE
// ============================================================================

import { API_BASE_URL } from '@types';
import { Patient } from '@model/Patient';

// ============================================================================
// TYPES
// ============================================================================

export interface PatientResponse {
  data: {
    patients: Array<{
      value: string;
      label: string;
    }>;
    devices: any[];
  };
}

export interface PatientOption {
  value: string;
  label: string;
  user: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    full_name: string;
  };
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

export const getPatientsAndDevices = async (): Promise<PatientResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/services/getPatientsAndDevices`,
    {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response.json();
};

export const getPatientsAndDevicesSafe = async (): Promise<PatientOption[]> => {
  try {
    const response = await getPatientsAndDevices();
    const patientsData = response.data?.patients || [];

    const patientOptions: PatientOption[] = patientsData.map(
      (patient: any) => ({
        value: patient.value,
        label: patient.label,
        user: {
          id: parseInt(patient.value),
          email: patient.label,
          first_name: patient.label.split(' ')[0] || '',
          last_name: patient.label.split(' ').slice(1).join(' ') || '',
          full_name: patient.label,
        },
      })
    );

    return patientOptions;
  } catch (error) {
    console.error('Error fetching patients:', error);
    return [];
  }
};

export const createPatient = async (code: string): Promise<any> => {
  const response = await fetch(`${API_BASE_URL}/services/createPatient`, {
    method: 'POST',
    body: JSON.stringify({ code }),
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    // Try to parse error response as JSON to extract message
    const contentType = response.headers.get('content-type');
    const text = await response.text();

    let errorMessage = text;
    if (contentType?.includes('application/json') && text) {
      try {
        const errorJson = JSON.parse(text);
        errorMessage = errorJson.message || errorJson.error || text;
      } catch {
        // If parsing fails, use the text as is
        errorMessage = text;
      }
    }

    const error = new Error(errorMessage);
    // Add status and parsed message to error for easier handling
    (error as any).status = response.status;
    (error as any).message = errorMessage;
    throw error;
  }

  // Check if response has content before trying to parse JSON
  const contentType = response.headers.get('content-type');
  const text = await response.text();

  // If response is empty or not JSON, return empty object
  if (!text || !contentType?.includes('application/json')) {
    return {};
  }

  try {
    return JSON.parse(text);
  } catch {
    // If JSON parsing fails, return empty object (success but no data)
    return {};
  }
};

export const createPatientSafe = async (
  code: string
): Promise<Patient | null> => {
  const result = await createPatient(code);
  // The API might return the created patient directly or in a nested structure
  // Try to extract the patient from the response
  if (result?.patient) {
    return result.patient;
  }
  if (result?.data?.patient) {
    return result.data.patient;
  }
  if (result?.id) {
    // If we only have an ID, return a minimal patient object
    return {
      id: result.id,
      user_id: result.user_id || result.userId || 0,
      code: code,
      created_at: result.created_at || new Date().toISOString(),
      updated_at: result.updated_at || new Date().toISOString(),
    };
  }
  // If we can't extract patient info, return null but still consider it a success
  // The refetch will get the new patient
  // Note: if createPatient throws an error, it will propagate up
  return null;
};

export const getPatientById = async (patientId: string): Promise<Patient> => {
  const response = await fetch(
    `${API_BASE_URL}/services/patients/${patientId}`,
    {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response.json();
};

export const getPatientByIdSafe = async (
  patientId: string
): Promise<Patient | null> => {
  try {
    return await getPatientById(patientId);
  } catch (error) {
    console.error('Error fetching patient by ID:', error);
    return null;
  }
};

export const updatePatient = async (
  patientId: string,
  data: Partial<Patient>
): Promise<Patient> => {
  const response = await fetch(
    `${API_BASE_URL}/services/patients/${patientId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(data),
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response.json();
};

export const updatePatientSafe = async (
  patientId: string,
  data: Partial<Patient>
): Promise<Patient | null> => {
  try {
    return await updatePatient(patientId, data);
  } catch (error) {
    console.error('Error updating patient:', error);
    return null;
  }
};

export const deletePatient = async (patientId: string): Promise<void> => {
  const response = await fetch(
    `${API_BASE_URL}/services/patients/${patientId}`,
    {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }
};

export const deletePatientSafe = async (
  patientId: string
): Promise<boolean> => {
  try {
    await deletePatient(patientId);
    return true;
  } catch (error) {
    console.error('Error deleting patient:', error);
    return false;
  }
};
